import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import App from './App.tsx'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { installGlobalErrorHandlers } from './utils/clientLog'
import { initTelemetry, stopTelemetry } from './utils/telemetry'
import { initPageViewTracking, stopPageViewTracking } from './lib/pageViewTracker'
import { hasConsent, subscribeConsent } from './lib/consent'
import { createAnalyticsController } from './lib/analyticsGating'
import { ErrorBoundary } from './components/ErrorBoundary'
import { runOnFirstInteraction } from './utils/firstInteraction'

installGlobalErrorHandlers()

const origError = console.error.bind(console);
console.error = (...args) => {
  if (args.length > 0 && typeof args[0] === 'string' && args[0].includes('identity provider')) return;
  origError(...args);
};

// Gating GDPR: telemetria, page-view e Sentry partono SOLO con consenso analytics.
// Sentry resta dietro import dinamico (l'SDK non e' mai nel critical path).
let sentryStarted = false;
let sentryAllowed = false;

function maybeInitSentry(): void {
  sentryAllowed = true;
  if (sentryStarted) return;
  sentryStarted = true;
  const initSentry = () => {
    // Consenso revocato mentre l'init era schedulato: non inizializzare un SDK non piu' voluto.
    if (!sentryAllowed) return;
    import('./lib/sentry')
      .then((m) => { if (sentryAllowed) m.initBrowserSentry({}); })
      .catch(() => {});
  };
  const isElectron = !!(window as any).electronAPI || window.location.protocol === 'file:';
  if (import.meta.env.PROD && !isElectron) {
    // Web pubblico: niente @sentry durante il load (contende banda e main thread
    // al first paint). Parte alla prima interazione o al massimo dopo 10s.
    runOnFirstInteraction(initSentry, 10000);
  } else if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(initSentry);
  } else {
    setTimeout(initSentry, 2000);
  }
}

// Revoca: disabilita l'init pendente, resetta il guard (ri-consenso -> nuova init)
// e chiude il client se gia' inizializzato.
function teardownSentry(): void {
  sentryAllowed = false;
  sentryStarted = false;
  import('./lib/sentry').then((m) => m.stopSentry()).catch(() => {});
}

// Controller simmetrico: start avvia telemetria+page-view+Sentry, stop li ferma tutti.
const analytics = createAnalyticsController({
  start: () => {
    initTelemetry({ appName: 'jarvis', appVersion: __APP_VERSION__ })
    initPageViewTracking()
    maybeInitSentry()
  },
  stop: () => {
    stopPageViewTracking()
    stopTelemetry()
    teardownSentry()
  },
});

// SEMPRE registrato (anche con consenso gia' presente al boot): la revoca ferma il page-view
// e la telemetria, un successivo ri-consenso riavvia tutto. Nessun flag monodirezionale.
subscribeConsent((state) => analytics.apply(state.categories.analytics));
analytics.apply(hasConsent('analytics'));

window.__APP_VERSION__ = __APP_VERSION__
window.__APP_VERSION_RAW__ = __APP_VERSION_RAW__
window.__BUILD_VERSION__ = __APP_VERSION__
window.__BUILD_ID__ = __BUILD_ID__

async function seedOsLocale() {
  try {
    const electronAPI = window.electronAPI;
    if (electronAPI && typeof electronAPI.getLocale === 'function') {
      const locale = await electronAPI.getLocale();
      console.info('[i18n] IPC getLocale() returned:', locale);
      if (locale) window.__JARVIS_OS_LOCALE__ = locale;
    }
  } catch {
    // IPC non disponibile (web build)
  }
}

async function bootstrap() {
  await seedOsLocale();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <AuthProvider>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </AuthProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </StrictMode>,
  )
}

void bootstrap();